import React, { useState } from "react";
import { ArrowUpRight, Check, GitBranch, RefreshCw , Flag } from "lucide-react";

// One filled "6"-shaped mark in a 4×6.5 box. Lucide's stroked Quote smears
// into a blob at this size.
const QUOTE_MARK = "M0 4.5C0 2.2 1.4 0.6 3.6 0L4 0.9C2.9 1.3 2.3 1.9 2.1 2.5A2 2 0 1 1 0 4.5Z";

// “ in the top-left corner, and the same pair turned 180° into ” in the
// bottom-right.
const QuoteMarks = ({ size = 14 }) => {
  const opening = (
    <>
      <path d={QUOTE_MARK} transform="translate(1.5 1.5) scale(1.3)" />
      <path d={QUOTE_MARK} transform="translate(7.8 1.5) scale(1.3)" />
    </>
  );
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      {opening}
      <g transform="rotate(180 12 12)">{opening}</g>
    </svg>
  );
};

// Types that name themselves; anything else falls back to the file extension.
const TYPE_LABEL = { kb: "KB", cases: "Case", bugs: "Bug", mermaid: "Flow" };

/**
 * What KIND of thing this card opens — "PDF", "KB", "Case".
 *
 * `source_type` alone is too coarse: every runbook is "docs", which tells the
 * engineer nothing about whether they are about to open a PDF or a web page.
 * So named types win, and "docs" falls through to the real file extension.
 */
const docTypeLabel = (sourceType, citation) => {
  const t = String(sourceType || "docs").toLowerCase();
  if (TYPE_LABEL[t]) return TYPE_LABEL[t];
  const path = String(citation || "").split(/[?#]/)[0];
  const ext = (path.match(/\.([a-z0-9]{2,5})$/i) || [])[1];
  return ext ? ext.toUpperCase() : "Doc";
};

/**
 * Colour carries exactly one meaning on these cards: green = the answer cited
 * it. Everything else is gray/cyan.
 *
 * The score badge used to run a green/amber/red scale and the rail and type
 * chip were tinted per source type, so a row of four cards showed five colours
 * and red shouted "error" at a 27% relevance score — which is not a problem,
 * just a weaker match. Retrieval scores are not a health signal, so they no
 * longer look like one.
 */

/**
 * SourceCard — single source document card. Two render modes:
 *  - isPreview=true, no whySelected yet: title + score, shimmering why area
 *  - enriched: title + score + "Why Selected"
 *
 * isCited means the answer actually cited this source (it carries an [S…] pill
 * in the prose) — shown as a green tick. That replaces the old "Sources
 * Selected" table: which sources were picked now reads off the cards.
 *
 * The raw URL is not rendered in the card BODY. A signed S3 or Salesforce
 * Lightning link runs well past the card width, so it truncated into
 * meaningless noise ("https://zscaler--zsuat.sandbox.lightning.force.com/…")
 * and needed its own Show more/less toggle to be readable at all. The whole
 * card is the link; the type chip says what opens and the arrow says where.
 *
 * It is still shown in full on the hover tooltip — a link the engineer cannot
 * inspect before clicking is worse than an ugly one, so the destination stays
 * available, just out of the layout.
 */
const SourceCard = ({ number = 1, title, sourceType = "docs", score = 0, whySelected, citation, url, isPreview = false, isActive = false, isCited = false, isCausal = false, canOpenMap = false, canReplace = false, canCite = false, replacing = false, citing = false, actionsLocked = false, pickerOpen = false, alternatives = [], onClick, onOpenMap, onToggleReplace, onReplace, onCite, onFlag }) => {
  const pct = Math.round(Math.max(0, Math.min(1, score)) <= 1 ? score * 100 : score);
  const showShimmer = isPreview && !whySelected;
  const type = docTypeLabel(sourceType, citation);

  // The card hides the URL, so the tooltip is the only place the engineer can
  // see where a click actually goes. It must therefore show the SAME value the
  // click handler opens — `citation || url`, matching CopilotPanel — or the
  // transparency is a lie the first time the two differ. `citation` carries the
  // "#page=N" of the cited page, so the tooltip shows the page too.
  const href = citation || url || "";
  const tooltip = [
    `${title || "Untitled source"}${isCited ? " — cited in this answer" : ""}`,
    href,
    href ? "Opens in a new tab" : "",
  ].filter(Boolean).join("\n");

  // Native tooltips stack: over a button inside the card, the card's own
  // tooltip (the document name) shows on top of the button's. Dropped while
  // the pointer is on a control so only the control's tooltip shows.
  const [overControl, setOverControl] = useState(false);
  const controlHover = {
    onMouseEnter: () => setOverControl(true),
    onMouseLeave: () => setOverControl(false),
  };

  return (
    // div[role=button], not <button>: the causal chip below is itself a button,
    // and a button inside a button is invalid HTML — React renders it, but the
    // nested click target stops behaving consistently across browsers. Keyboard
    // activation is restored by hand so the card is no less operable than before.
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter") onClick?.();
        if (e.key === " ") { e.preventDefault(); onClick?.(); }
      }}
      className={`d5-source-card ${showShimmer ? "d5-source-card-loading" : ""} ${isActive ? "d5-source-card-active" : ""} ${isCited ? "d5-source-card-cited" : ""}`}
      // The card's native tooltip would otherwise stay up over the picker and
      // name the source being replaced instead of the option under the cursor.
      title={pickerOpen || overControl ? undefined : tooltip}
      // A control that unmounts under the pointer (Cite → spinner) never fires
      // its own mouseleave.
      onMouseLeave={() => setOverControl(false)}
    >
      <div className="d5-source-card-top">
        <span className="d5-source-index">{number}</span>
        <span className="d5-source-title">{title || "Untitled source"}</span>
        <span className="d5-source-card-badges">
          {isCited && (
            <span className="d5-source-tick" title="Cited in this answer" {...controlHover}>
              <Check size={9} strokeWidth={3.5} />
            </span>
          )}
          {!showShimmer && <span className="d5-score-badge">{pct}%</span>}
        </span>
      </div>

      {showShimmer ? (
        <>
          <div className="d5-shimmer-block" aria-hidden="true" />
          <div className="d5-shimmer-block" aria-hidden="true" />
        </>
      ) : (
        whySelected && (
          <div className="d5-source-why">
            <span className="d5-source-why-label">Why selected</span>
            <span className="d5-source-why-text">{whySelected}</span>
          </div>
        )
      )}

      <div className="d5-source-meta">
        <span className="d5-source-doctype">{type}</span>
        {(canCite || citing) && (
          <button
            type="button"
            className="d5-source-replace-btn d5-source-cite-btn"
            disabled={actionsLocked}
            title="Regenerate the answer so it uses and cites this source"
            aria-label={citing ? "Citing this source…" : "Cite this source"}
            {...controlHover}
            onClick={(e) => {
              e.stopPropagation();
              onCite?.();
            }}
          >
            {citing
              ? <RefreshCw size={11} strokeWidth={2.5} className="d5-source-replace-spin" />
              : <QuoteMarks size={14} />}
          </button>
        )}
        {(canReplace || replacing) && (
          <button
            type="button"
            className="d5-source-replace-btn"
            disabled={actionsLocked}
            aria-expanded={pickerOpen}
            title="Replace this source and regenerate the answer"
            aria-label={replacing ? "Replacing this source…" : "Replace this source"}
            {...controlHover}
            onClick={(e) => {
              e.stopPropagation();
              onToggleReplace?.();
            }}
          >
            <RefreshCw size={11} strokeWidth={2.5} className={replacing ? "d5-source-replace-spin" : ""} />
          </button>
        )}
        {/* A causal card with no resolvable document name has no map to open —
            a button that always lands on "no diagnostic graph found" is worse
            than no button. Against a backend that predates the file_name /
            file_names fields the chip stays exactly what it was: a static
            marker, not a dead link. */}
        {isCausal && (canOpenMap ? (
          <button
            type="button"
            className="d5-source-causal d5-source-causal-btn"
            title="Open Source Map"
            aria-label="Open Source Map"
            {...controlHover}
            onClick={(e) => { e.stopPropagation(); onOpenMap?.(); }}
          >
            <GitBranch size={11} strokeWidth={2.5} />
          </button>
        ) : (
          <span className="d5-source-causal" title="Has diagnostic knowledge graph" {...controlHover}>
            <GitBranch size={9} strokeWidth={3} />
          </span>
        ))}
        {/* LEARNING: "this source does not belong on this question". Rendered
            only when a handler is passed, so every existing call site is
            unchanged. Reporting is one click; the composer asks for one
            sentence. */}
        {onFlag && (
          <button
            type="button"
            className="d5-source-flag"
            title="This source is wrong for this question"
            aria-label="Report this source as wrong"
            onClick={(e) => { e.stopPropagation(); onFlag(); }}
          >
            <Flag size={9} strokeWidth={3} />
          </button>
        )}
        {citation && (
          <ArrowUpRight className="d5-source-open" size={13} strokeWidth={2.5} aria-hidden="true" />
        )}
      </div>
      {pickerOpen && canReplace && (
        <div
          className="d5-source-replace-menu"
          role="menu"
          aria-label={`Replacement sources for source ${number}`}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          {alternatives.map((candidate) => {
            const candidateTitle = candidate.title || "Untitled source";
            const fileName = String(candidate.file_name || "").trim();
            const showFile = fileName && fileName.toLowerCase() !== candidateTitle.toLowerCase();
            const candidateScore = Math.max(0, Number(candidate.score) || 0);
            return (
              <button
                key={candidate.candidate_id}
                type="button"
                role="menuitem"
                className="d5-source-replace-option"
                title={[candidateTitle, showFile ? fileName : ""].filter(Boolean).join("\n")}
                onClick={(e) => {
                  e.stopPropagation();
                  onReplace?.(candidate);
                }}
              >
                <span className="d5-source-replace-option-name">
                  <span className="d5-source-replace-option-title">{candidateTitle}</span>
                  {showFile && (
                    <span className="d5-source-replace-option-file">{fileName}</span>
                  )}
                </span>
                <span className="d5-source-replace-option-score">
                  {Math.round(candidateScore <= 1 ? candidateScore * 100 : candidateScore)}%
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SourceCard;
